import {
  FulfillRequest,
  StorePendingRequest,
} from "../store/slices/storeInwardSlice";
import axiosSetup from "../utils/axiosSetup";
import toast from "react-hot-toast";

export const fetchRawMaterialsByWarehouseApi = async ({
  warehouseId,
  page = 1,
  limit = 10,
  search = "",
}: {
  warehouseId?: string;
  page?: number;
  limit?: number;
  search?: string;
}) => {
  try {
    const res = await axiosSetup.get(
      "/warehouse/store/raw-materials-by-warehouse",
      {
        params: { warehouseId, page, limit, search, t: Date.now() },
      },
    );
    return res.data.data;
  } catch (error) {
    toast.error("Failed to fetch raw materials");
    throw error;
  }
};

export const createStoreInwardApi = async (payload: any) => {
  try {
    const res = await axiosSetup.post(
      "/warehouse/store/create/inward-request",
      payload,
    );
    return res.data.data;
  } catch (error) {
    toast.error("Failed to create store inward");
    throw error;
  }
};

export const fetchDropdownDataApi = async (usageType?: string) => {
  try {
    const res = await axiosSetup.get("/warehouse/store/dropdown-data", {
      params: { usageType: usageType || "warehouse" },
    });
    return res.data.data;
  } catch (error) {
    toast.error("Failed to fetch dropdown data");
    throw error;
  }
};

export const fetchStoreApprovedRequestsApi = async ({
  page = 1,
  limit = 10,
  search = "",
}: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<{
  data: StorePendingRequest[];
  pagination: { totalItems: number; totalPages: number; currentPage: number };
}> => {
  try {
    const res = await axiosSetup.get("/warehouse/store/pending", {
      params: { page, limit, search, t: Date.now() },
    });
    return res.data.data;
  } catch (error) {
    toast.error("Failed to fetch approved requests");
    throw error;
  }
};

export const fulfillStoreRequestApi = async (payload: FulfillRequest[]) => {
  try {
    const res = await axiosSetup.post("/warehouse/store/fulfill-request", {
      requests: payload,
    });
    return res.data;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to process requests";
    throw new Error(errorMessage);
  }
};

export const LowStockRawMaterialsListApi = async ({
  page = 1,
  limit = 10,
  search = "",
}: {
  page?: number;
  limit?: number;
  search?: string;
}) => {
  try {
    const res = await axiosSetup.get("/warehouse/store/low-stock", {
      params: { page, limit, search, t: Date.now() },
    });
    return res.data.data;
  } catch (error) {
    toast.error("Failed to fetch low-stock materials");
    throw error;
  }
};

export const fetchStoreStockAdminViewPaginationApi = async ({
  page = 1,
  limit = 10,
  search = "",
}: {
  page?: number;
  limit?: number;
  search?: string;
}) => {
  try {
    const res = await axiosSetup.get("/warehouse/admin/store-stock", {
      params: { page, limit, search, t: Date.now() },
    });
    return res.data.data;
  } catch (error) {
    toast.error("Failed to fetch store stock");
    throw error;
  }
};

export const downloadStoreStockAdminReportDownloadApi = async ({
  format,
  search = "",
  date,
}: {
  format: "xlsx" | "csv";
  search?: string;
  date: string;
}) => {
  try {
    const res = await axiosSetup.get("/warehouse/admin/store-stock/download", {
      params: { format, search, date },
      responseType: "blob",
    });
    return res.data;
  } catch (error) {
    throw new Error("Failed to generate and download store stock report.");
  }
};

export const fetchStorePriceByWarehouseApi = async () => {
  try {
    const res = await axiosSetup.get("/warehouse/admin/store-stock-price");
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const createMaterialOutwardApi = async (payload: any) => {
  try {
    const res = await axiosSetup.post(
      "/warehouse/outward/material-production",
      payload,
    );
    return res.data.data;
  } catch (error: any) {
    const msg = error.response?.data?.message || "Failed to initiate outward";
    toast.error(msg);
    throw error;
  }
};

export const fetchPendingOutwardRequestsApi = async () => {
  try {
    const res = await axiosSetup.get("/warehouse/material-outward/pending");
    return res.data.data;
  } catch (error: any) {
    const msg =
      error.response?.data?.message || "Failed to fetch pending requests";
    toast.error(msg);
    throw error;
  }
};

export const submitMaterialInwardFromOutwardApi = async (payload: any) => {
  try {
    const res = await axiosSetup.post(
      "/warehouse/material-inward/from-outward",
      payload,
    );
    return res.data.data;
  } catch (error: any) {
    const msg = error.response?.data?.message || "Failed to submit inward";
    toast.error(msg);
    throw error;
  }
};

export const updateStoreInwardApi = async (payload: any) => {
  const { id, ...data } = payload;
  const response = await axiosSetup.put(
    `/warehouse/update-store-inward/${id}`,
    data,
  );
  return response.data;
};
